import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  Injectable,
  Input,
  Optional,
  Output,
  Renderer2,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

import { createRenderScheduler } from '@recruitee/common';
import { asViewRef } from '@recruitee/utils';

@Directive()
@Injectable()

// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class RtInput<V> implements ControlValueAccessor {
  public get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  public set disabled(value: boolean) {
    this._disabled = value;
  }
  @Input() public value: V | null = null;
  @Input() public readOnly: boolean = false;

  @Output() public valueChange: EventEmitter<V> = new EventEmitter();
  @Output() public focus: EventEmitter<UIEvent> = new EventEmitter();
  @Output() public blur: EventEmitter<UIEvent> = new EventEmitter();

  private _disabled: boolean = false;
  private renderScheduler = createRenderScheduler();

  constructor(
    public elementRef: ElementRef,
    protected renderer2: Renderer2,
    @Optional()
    @Self()
    protected ngControl: NgControl,
    protected cdRef: ChangeDetectorRef,
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  public get nativeInputElement(): HTMLInputElement {
    return this.elementRef.nativeElement.querySelector('input, textarea');
  }

  public writeValue(value: V | undefined): void {
    if (value !== undefined) {
      this.setValue(value);
      // TODO remove this.
      // This is left for backwards compability with components using this component with formControl
      // but not subscribing itself to its changes, while they should.
      // (parent component should listen to changes of its' form controls and update itself when needed,
      //  not the opposite: child shouldn't decide about when a parent should be updated)
      if (!asViewRef(this.cdRef).destroyed) {
        this.renderScheduler.schedule();
      }
    }
  }

  public registerOnChange(fn: (value: V) => void): void {
    this.valueChange.subscribe(fn);
  }

  public registerOnTouched(fn: () => void): void {
    this.blur.subscribe(fn);
  }

  public setDisabledState(disabled: boolean): void {
    this._disabled = disabled;
    if (!asViewRef(this.cdRef).destroyed) {
      this.renderScheduler.schedule();
    }
  }

  public handleChange(value: V): void {
    this.valueChange.emit(value);

    // Mutate the value only when controlled by a FormControl
    if (this.ngControl) {
      this.setValue(value);
    }
  }

  protected setValue(value: V): void {
    this.value = value;
    if (this.ngControl && !asViewRef(this.cdRef).destroyed) {
      this.renderScheduler.schedule();
    }
  }
}

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class RtTextInput extends RtInput<string> {
  public insertAtCaret(
    value: string | { toString: () => string },
    type: 'plaintext' | 'html' | 'placeholder' | 'link',
  ): void;
  public insertAtCaret(value: string): void {
    const str = value.toString();
    const caretPosition = this.nativeInputElement.selectionStart!;
    const caretPositionAfterChange = caretPosition + str.length;
    const templateText = this.nativeInputElement.value;

    this.handleChange(
      `${templateText.substring(0, caretPosition)}${str}${templateText.substring(caretPosition)}`,
    );

    this.nativeInputElement.focus();
    this.nativeInputElement.setSelectionRange(caretPositionAfterChange, caretPositionAfterChange);
  }
}
